import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser, useCompany } from '../../contexts/AuthContext';
import estimateService from '../../services/api/estimateService';
import { customersService, productsService } from '../../services/api';
import CustomerCreateModal from '../shared/CustomerCreateModal';
import {
  calculateLineItem,
  calculateEstimateTotals,
  formatDateForInput,
  validateEstimate,
  getDefaultEstimateData,
  getDefaultLineItem,
  formatCurrency
} from '../../utils/estimateHelpers';
import FieldError from '../shared/FieldError';
import { discountCodeFieldError, DISCOUNT_ERRORS } from '../../constants/discountErrors';
import {
  parseDateSafely,
  resolveGenericLineItemType,
  resolveCatalogLineIdentity,
  resolveCatalogLineIdentityFromVariant,
  applyCatalogIdentityToLine,
  shouldClearCatalogLink,
  clearCatalogLinkOnLine,
  clearCatalogProductIdentity,
  buildCatalogPickerRows,
  toDocumentLineItemPayload,
  namedDiscountLines,
  lineItemsSubtotalCents,
  capManualDiscountCents,
  hydrateManualDiscount,
  persistManualDiscount
} from '../../utils/invoiceHelpers';
import CatalogProductPickerSection from '../shared/CatalogProductPickerSection';
import { DatePicker } from '../ui';
import { format } from "date-fns";
import { cn } from '../../lib/utils';
import {
  buildMerchantInfoFromCompany,
  scrubMerchantPlaceholder,
  fillEmptyMerchantInfoFromCompany,
  getCompanyContactKey
} from '../../utils/merchantDisplay';

/**
 * @description Estimate creation and editing form with Untitled UI-inspired design
 *
 * Key features:
 * - Clean, modern design mirroring InvoiceForm visual hierarchy
 * - Responsive layout for desktop and mobile
 * - Customer search with dropdown selection
 * - Product/service line items with search
 * - Real-time total calculations
 * - Expiration date (instead of due date) — defaults to issue date + 30 days
 * - Save Draft and Save & Send actions
 * - Edit mode restricted to draft status only
 *
 * @dependencies
 * - estimateService: API calls for estimate CRUD and status transitions
 * - estimateHelpers: Calculation, validation, default data, formatting
 * - customersService: Fetch customer data
 * - productsService: Fetch product catalog
 * - CustomerCreateModal: Inline customer creation
 *
 * @notes
 * - Wave 3 (LYN-249): keep the $ Discount field; coupon + live evaluate
 *   add named engine rows beside it. Persist is server-side.
 * - No payment methods selection (estimates don't accept payments)
 * - No "Balance Due" in sidebar (estimates don't track payments)
 * - Expiration date auto-updates when issue date changes (issue_date + 30 days)
 * - Quick-add Sales persists item_type=product (LYN-243). Catalog lines copy
 *   products.item_type (bundle → product). Products with variants nest under
 *   the parent in the Item typeahead (LYN-377); picking a variant sets
 *   productId + variantId, name, description, and unit price. Fallback
 *   first-variant attach remains so convert-to-invoice does not hit
 *   VARIANT_REQUIRED.
 * - Status default: 'draft' (not 'open')
 * - URL patterns: /merchant/estimates/new, /merchant/estimates/:estimateNumber/edit
 */
function EstimateForm() {
  const { user } = useUser();
  const { company } = useCompany();
  const navigate = useNavigate();
  const { estimateNumber } = useParams();
  const isEdit = !!estimateNumber;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [preGeneratedEstimateNumber, setPreGeneratedEstimateNumber] = useState('');

  // Customer data
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Product data
  const [products, setProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState({});

  // Estimate data
  const [estimateData, setEstimateDataRaw] = useState(() => {
    const data = getDefaultEstimateData();
    return data;
  });

  const setEstimateData = setEstimateDataRaw;
  const [items, setItems] = useState([{ ...getDefaultLineItem(), isTaxable: false }]);
  const [showShipping, setShowShipping] = useState(false);
  const [showMerchantEdit, setShowMerchantEdit] = useState(false);
  const [engineApplied, setEngineApplied] = useState([]);
  const [codeFieldError, setCodeFieldError] = useState(null);

  // Merchant display — hydrated from Company Settings (per-doc overrides only)
  const [merchantInfo, setMerchantInfo] = useState(() =>
    buildMerchantInfoFromCompany(company, user)
  );
  const [merchantHydratedFromDoc, setMerchantHydratedFromDoc] = useState(false);
  const [merchantSeededFromCompany, setMerchantSeededFromCompany] = useState(() => !!company);
  const companyContactKey = useMemo(() => getCompanyContactKey(company), [company]);
  const lastCompanyContactKeyRef = useRef(companyContactKey);

  const getCustomerName = useCallback((customer) => {
    if (!customer) return '';
    return customer.company ||
           `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  }, []);

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!user?.id) return;
      try {
        const customersData = await customersService.getMerchantCustomers();
        setCustomers(customersData || []);
      } catch (err) {
        // Error silenced — customer list failure is non-blocking
      }
    };
    fetchCustomers();
  }, [user]);

  // Seed From from Company Settings on create; re-apply when Settings contact
  // fingerprint changes (unless the per-doc From editor is open).
  useEffect(() => {
    if (!company || isEdit) return;
    if (merchantHydratedFromDoc) return;

    const contactKeyChanged = lastCompanyContactKeyRef.current !== companyContactKey;
    lastCompanyContactKeyRef.current = companyContactKey;

    if (!merchantSeededFromCompany) {
      setMerchantInfo(buildMerchantInfoFromCompany(company, user));
      setMerchantSeededFromCompany(true);
      return;
    }

    if (contactKeyChanged && !showMerchantEdit) {
      setMerchantInfo(buildMerchantInfoFromCompany(company, user));
      return;
    }

    setMerchantInfo((prev) => fillEmptyMerchantInfoFromCompany(prev, company, user));
  }, [company, user, isEdit, merchantHydratedFromDoc, merchantSeededFromCompany, companyContactKey, showMerchantEdit]);

  // Edit mode: fill empty snapshot fields from company after doc hydrate
  useEffect(() => {
    if (!isEdit || !company || !merchantHydratedFromDoc) return;
    setMerchantInfo((prev) => fillEmptyMerchantInfoFromCompany(prev, company, user));
  }, [company, user, isEdit, merchantHydratedFromDoc]);

  // Fetch next estimate number for create mode
  useEffect(() => {
    const fetchNextEstimateNumber = async () => {
      if (!isEdit && user?.id) {
        try {
          const response = await estimateService.getNextEstimateNumber();
          setPreGeneratedEstimateNumber(response.estimateNumber);
        } catch (error) {
          // Fallback to temp number — handled below
          const tempNumber = `EST-${Date.now().toString().slice(-6)}`;
          setPreGeneratedEstimateNumber(tempNumber);
        }
      }
    };
    fetchNextEstimateNumber();
  }, [isEdit, user]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      if (!user?.id) return;
      try {
        const productsData = await productsService.getProducts();
        setProducts(Array.isArray(productsData) ? productsData : (productsData?.products || []));
      } catch (err) {
        // Silent fail
      }
    };
    fetchProducts();
  }, [user]);

  // Fetch existing estimate for edit mode
  useEffect(() => {
    if (!isEdit || !estimateNumber || estimateNumber === 'undefined') {
      if (isEdit && (!estimateNumber || estimateNumber === 'undefined')) {
        setError('Invalid estimate number. Please navigate from the estimate list.');
      }
      return;
    }

    const fetchEstimate = async () => {
      try {
        setLoading(true);
        const response = await estimateService.getEstimate(estimateNumber);
        const estimate = response.estimate || response;
        const responseItems = response.items || estimate.items || [];

        // Only allow editing draft estimates
        if (estimate.status && estimate.status !== 'draft') {
          navigate(`/merchant/estimates/${estimateNumber}`, { replace: true });
          return;
        }

        setEstimateData({
          estimateNumber: estimate.estimateNumber,
          customerId: estimate.customerId,
          issueDate: formatDateForInput(estimate.issueDate || estimate.estimateDate),
          expirationDate: formatDateForInput(estimate.expirationDate),
          paymentTerms: estimate.paymentTerms || 'net_30',
          poNumber: estimate.poNumber || '',
          status: estimate.status || 'draft',
          taxRate: estimate.taxRate || 0,
          ...hydrateManualDiscount(estimate),
          discountCode: estimate.discountCode || '',
          shippingCost: estimate.shippingCost || 0,
          shippingCarrier: estimate.shippingCarrier || '',
          shippingTracking: estimate.shippingTracking || '',
          internalNotes: estimate.internalNotes || '',
          customerNotes: estimate.customerNotes || '',
          terms: estimate.terms || '',
          footer: estimate.footer || ''
        });

        if (
          estimate.merchantDisplayName
          || estimate.merchantDisplayPhone
          || estimate.merchantDisplayEmail
          || estimate.merchantDisplayAddress
          || estimate.merchantDisplayWebsite
        ) {
          const streetRaw = estimate.merchantDisplayAddress || '';
          const street = scrubMerchantPlaceholder(streetRaw);
          setMerchantInfo({
            displayName: scrubMerchantPlaceholder(estimate.merchantDisplayName) || company?.businessName || '',
            displayAddress: street || company?.addressLine1 || '',
            displayCity: scrubMerchantPlaceholder(estimate.merchantDisplayCity, { street: streetRaw }) || company?.city || '',
            displayState: scrubMerchantPlaceholder(estimate.merchantDisplayState, { street: streetRaw }) || company?.state || '',
            displayZip: scrubMerchantPlaceholder(estimate.merchantDisplayZip || estimate.merchantDisplayPostalCode, { street: streetRaw }) || company?.postalCode || '',
            displayEmail: scrubMerchantPlaceholder(estimate.merchantDisplayEmail) || company?.email || user?.email || '',
            displayPhone: scrubMerchantPlaceholder(estimate.merchantDisplayPhone) || company?.phone || '',
            displayWebsite: scrubMerchantPlaceholder(estimate.merchantDisplayWebsite) || company?.website || '',
            logoUrl: estimate.merchantLogoUrl || null
          });
          setMerchantHydratedFromDoc(true);
        } else {
          setMerchantInfo(buildMerchantInfoFromCompany(company, user));
          setMerchantHydratedFromDoc(true);
        }

        if (estimate.hasShippingAddress) {
          setShowShipping(true);
          setEstimateData(prev => ({
            ...prev,
            shipToName: estimate.shipToName || '',
            shipToAddress: estimate.shipToAddress || '',
            shipToCity: estimate.shipToCity || '',
            shipToState: estimate.shipToState || '',
            shipToZip: estimate.shipToZip || ''
          }));
        }

        if (responseItems && responseItems.length > 0) {
          setItems(responseItems.map(item => ({
            id: item.id,
            productId: item.productId,
            variantId: item.variantId || null,
            variantName: item.variantName || null,
            itemName: item.itemName || '',
            itemType: item.itemType || 'product',
            description: item.description || '',
            date: formatDateForInput(item.date || estimate.issueDate || estimate.estimateDate),
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || 0,
            isTaxable: item.isTaxable || false,
            discountPercent: item.discountPercent || 0,
            discountAmount: item.discountAmount || 0,
            deductInventory: item.deductInventory || false
          })));
        }

        const persisted = estimate.appliedDiscounts || [];
        setEngineApplied(persisted.filter((row) => row.method !== 'manual'));
      } catch (err) {
        // Error handled via UI state
        setError('Failed to load estimate');
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
    // company/user used only as empty-field fallbacks at hydrate time; omit from
    // deps so company context refresh does not refetch and stomp From edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, estimateNumber, setEstimateData, navigate]);

  // Sync selected customer when editing
  useEffect(() => {
    if (isEdit && estimateData.customerId && customers.length > 0 && !selectedCustomer) {
      const customerIdStr = String(estimateData.customerId);
      const customer = customers.find(c => String(c.id) === customerIdStr);
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerSearch(getCustomerName(customer));
      }
    }
  }, [isEdit, estimateData.customerId, customers, selectedCustomer, getCustomerName]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowProductDropdown({});
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cmd+S / Ctrl+S keyboard shortcut to save draft (ref updated after handleSave is defined)
  const saveRef = useRef(null);
  useEffect(() => {
    const handleSaveShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (saveRef.current) saveRef.current();
      }
    };
    window.addEventListener('keydown', handleSaveShortcut);
    return () => window.removeEventListener('keydown', handleSaveShortcut);
  }, []);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setEstimateData(prev => ({ ...prev, customerId: customer.id }));
    setCustomerSearch(getCustomerName(customer));
    setShowCustomerDropdown(false);
  };

  const handlePaymentTermsChange = (terms) => {
    setEstimateData(prev => ({ ...prev, paymentTerms: terms }));
  };

  const handleIssueDateChange = (newDate) => {
    // Auto-update expiration date: issue date + 30 days
    const issueDate = parseDateSafely(newDate);
    const expirationDate = new Date(issueDate);
    expirationDate.setDate(expirationDate.getDate() + 30);
    setEstimateData(prev => ({
      ...prev,
      issueDate: newDate,
      expirationDate: formatDateForInput(expirationDate)
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unitPrice' || field === 'discountPercent' || field === 'discountAmount') {
      const calc = calculateLineItem(newItems[index]);
      newItems[index].subtotal = calc.subtotal;
      newItems[index].total = calc.total;
    }
    setItems(newItems);
  };

  const handleProductSelect = async (index, product) => {
    const identity = await resolveCatalogLineIdentity(
      product,
      (productId) => productsService.getVariants(productId)
    );
    setItems((prev) => applyCatalogIdentityToLine(prev, index, identity));
  };

  const handleVariantSelect = (index, product, variant) => {
    const identity = resolveCatalogLineIdentityFromVariant(product, variant);
    setItems((prev) => applyCatalogIdentityToLine(prev, index, identity));
  };

  const handleItemNameEdit = (index, value, dropdownKey) => {
    setItems((prev) => {
      const next = prev.slice();
      let line = { ...next[index], itemName: value };
      if (line.productId) {
        const prod = products.find((p) => p.id === line.productId);
        if (shouldClearCatalogLink(prod, value, line)) {
          line = clearCatalogLinkOnLine(line);
          line.itemName = value;
        }
      }
      next[index] = line;
      return next;
    });
    setShowProductDropdown((prev) => ({ ...prev, [dropdownKey]: true }));
  };

  const addLineItem = () => {
    const newItem = {
      ...getDefaultLineItem(),
      date: items[0]?.date || formatDateForInput(new Date()),
      isTaxable: false
    };
    setItems([...items, newItem]);
  };

  const removeLineItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const engineDiscountCents = engineApplied.reduce(
    (sum, row) => sum + Number(row.amountCents || row.amount_cents || 0),
    0
  );
  const previewSubtotalCents = lineItemsSubtotalCents(items);
  const requestedManualCents = Math.round((parseFloat(estimateData.discountValue) || 0) * 100);
  const manualDiscountCents = capManualDiscountCents(
    previewSubtotalCents,
    engineDiscountCents,
    requestedManualCents
  );
  const totals = calculateEstimateTotals(items, {
    ...estimateData,
    appliedDiscountAmount: (manualDiscountCents + engineDiscountCents) / 100
  });
  const previewNamedDiscounts = namedDiscountLines(
    [
      ...engineApplied,
      ...(manualDiscountCents > 0
        ? [{ title: 'Discount', amountCents: manualDiscountCents }]
        : [])
    ],
    0
  );

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      const subtotalCents = lineItemsSubtotalCents(items);
      try {
        const result = await estimateService.evaluateDiscounts({
          items: items
            .filter((item) => item.productId)
            .map((item) => ({
              productId: item.productId,
              variantId: item.variantId || null
            })),
          subtotalCents,
          code: estimateData.discountCode
        });
        if (cancelled) return;
        setEngineApplied(result.appliedDiscounts || []);
        setCodeFieldError(discountCodeFieldError(result));
      } catch (_err) {
        if (cancelled) return;
        setCodeFieldError(DISCOUNT_ERRORS.EVALUATE_UNAVAILABLE);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [items, estimateData.discountCode]);

  const DROPDOWN_LIMIT = 25;
  const { displayed: filteredCustomers, totalMatches: totalCustomerMatches } = useMemo(() => {
    if (!customerSearch) return { displayed: customers, totalMatches: customers.length };
    const search = customerSearch.toLowerCase();
    if (selectedCustomer && getCustomerName(selectedCustomer).toLowerCase() === search) {
      return { displayed: [selectedCustomer], totalMatches: 1 };
    }
    const matched = customers.filter(c =>
      (c.company && c.company.toLowerCase().includes(search)) ||
      (c.firstName && c.firstName.toLowerCase().includes(search)) ||
      (c.lastName && c.lastName.toLowerCase().includes(search)) ||
      (c.email && c.email.toLowerCase().includes(search))
    );
    return { displayed: matched.slice(0, DROPDOWN_LIMIT), totalMatches: matched.length };
  }, [customerSearch, customers, selectedCustomer, getCustomerName]);

  const handleSave = async (action = null) => {
    const validation = validateEstimate({ ...estimateData, items });
    if (!validation.isValid) {
      setFormErrors(validation.errors);

      // Build user-friendly error messages
      const errorMessages = [];
      if (validation.errors.customer) {
        errorMessages.push('Select a customer');
      }
      if (validation.errors.issueDate) {
        errorMessages.push('Set an issue date');
      }
      if (validation.errors.items) {
        errorMessages.push('Add at least one line item');
      }

      // Check for item-specific errors
      const itemErrors = Object.keys(validation.errors).filter(key => key.startsWith('item_'));
      if (itemErrors.length > 0) {
        const itemIndices = [...new Set(itemErrors.map(key => {
          const match = key.match(/item_(\d+)_/);
          return match ? parseInt(match[1]) + 1 : null;
        }).filter(Boolean))];

        if (itemIndices.length === 1) {
          errorMessages.push(`Complete line item ${itemIndices[0]} (name, quantity, and price required)`);
        } else if (itemIndices.length > 1) {
          errorMessages.push(`Complete line items ${itemIndices.join(', ')} (name, quantity, and price required)`);
        }
      }

      setError(errorMessages);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const estimatePayload = {
        ...(isEdit ? { estimateNumber: estimateNumber } : {}),
        customerId: estimateData.customerId,
        issueDate: estimateData.issueDate,
        expirationDate: estimateData.expirationDate,
        paymentTerms: estimateData.paymentTerms,
        poNumber: estimateData.poNumber || null,
        status: 'draft',
        subtotal: totals.subtotal,
        taxableSubtotal: totals.taxableSubtotal,
        taxRate: estimateData.taxRate,
        taxAmount: totals.taxAmount,
        ...persistManualDiscount(estimateData.discountValue),
        discountCode: estimateData.discountCode || '',
        shippingCost: estimateData.shippingCost,
        shippingCarrier: showShipping ? estimateData.shippingCarrier : null,
        shippingTracking: showShipping ? estimateData.shippingTracking : null,
        totalAmount: totals.totalAmount,
        internalNotes: estimateData.internalNotes,
        customerNotes: estimateData.customerNotes,
        terms: estimateData.terms,
        footer: estimateData.footer,
        merchantDisplayName: merchantInfo.displayName,
        merchantDisplayAddress: merchantInfo.displayAddress,
        merchantDisplayCity: merchantInfo.displayCity,
        merchantDisplayState: merchantInfo.displayState,
        merchantDisplayPostalCode: merchantInfo.displayZip,
        merchantDisplayEmail: merchantInfo.displayEmail,
        merchantDisplayPhone: merchantInfo.displayPhone,
        merchantDisplayWebsite: merchantInfo.displayWebsite,
        merchantLogoUrl: merchantInfo.logoUrl,
        hasShippingAddress: showShipping,
        shipToName: showShipping ? estimateData.shipToName : null,
        shipToAddress: showShipping ? estimateData.shipToAddress : null,
        shipToCity: showShipping ? estimateData.shipToCity : null,
        shipToState: showShipping ? estimateData.shipToState : null,
        shipToPostalCode: showShipping ? estimateData.shipToZip : null,
        items: items.map((item, index) => toDocumentLineItemPayload(item, index))
      };

      let savedEstimate;
      if (isEdit) {
        savedEstimate = await estimateService.updateEstimate(estimateNumber, estimatePayload);
      } else {
        savedEstimate = await estimateService.createEstimate(estimatePayload);
      }

      const estNum = savedEstimate.estimateNumber || savedEstimate.estimate?.estimateNumber || savedEstimate.estimate_number;

      if (action === 'send') {
        // Save as draft first, then send to transition to 'sent' status
        try {
          await estimateService.sendEstimate(estNum);
        } catch (sendErr) {
          // Send failure is non-blocking — estimate was already saved
          // Even if send fails, estimate was saved — navigate to detail page
        }
        if (estNum) {
          navigate(`/merchant/estimates/${estNum}`);
        } else {
          navigate('/merchant/dashboard?tab=estimates');
        }
      } else if (estNum) {
        navigate(`/merchant/estimates/${estNum}`);
      } else {
        navigate('/merchant/dashboard?tab=estimates');
      }
    } catch (err) {
      // Error handled via UI state
      setError('Failed to save estimate. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Keep saveRef in sync with handleSave (for Cmd+S shortcut)
  saveRef.current = () => handleSave('save');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-medium text-gray-500">Loading estimate...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex items-center justify-between h-[72px] px-4 sm:px-8">
            <div className="flex items-center gap-5">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="hidden sm:block w-px h-6 bg-gray-200" />
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {isEdit ? 'Edit Estimate' : 'Create Estimate'}
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {estimateNumber || preGeneratedEstimateNumber || 'Draft'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => handleSave('save')}
                disabled={saving}
                className="inline-flex items-center h-10 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save draft'}
              </button>
              <button
                onClick={() => handleSave('send')}
                disabled={saving}
                className="inline-flex items-center h-10 px-5 text-sm font-semibold text-gray-900 bg-brand rounded-lg hover:bg-brand-hover transition-colors shadow-sm disabled:opacity-50"
              >
                Save & Send
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-4 sm:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800">
                  {Array.isArray(error) ? 'Please complete the following to save:' : 'Error'}
                </h3>
                {Array.isArray(error) ? (
                  <ul className="mt-2 space-y-1">
                    {error.map((msg, idx) => (
                      <li key={idx} className="text-sm text-red-700 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                        {msg}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-6">
            {/* Estimate Details Section */}
            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Estimate details</h2>
                <p className="text-sm text-gray-500 mt-1">Basic information about this estimate</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                    {!showMerchantEdit ? (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <p className="font-medium text-gray-900">{merchantInfo.displayName || 'Add company details'}</p>
                        {merchantInfo.displayAddress && (
                          <p className="text-sm text-gray-600 mt-1">{merchantInfo.displayAddress}</p>
                        )}
                        {(merchantInfo.displayCity || merchantInfo.displayState || merchantInfo.displayZip) && (
                          <p className="text-sm text-gray-600">
                            {[merchantInfo.displayCity, merchantInfo.displayState].filter(Boolean).join(', ')}
                            {merchantInfo.displayZip ? ` ${merchantInfo.displayZip}` : ''}
                          </p>
                        )}
                        {merchantInfo.displayPhone && (
                          <p className="text-sm text-gray-600 mt-1">{merchantInfo.displayPhone}</p>
                        )}
                        {merchantInfo.displayEmail && (
                          <p className="text-sm text-gray-600">{merchantInfo.displayEmail}</p>
                        )}
                        {merchantInfo.displayWebsite && (
                          <p className="text-sm text-gray-600">{merchantInfo.displayWebsite}</p>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowMerchantEdit(true)}
                          className="mt-3 text-sm font-medium text-brand hover:text-brand-hover transition-colors"
                        >
                          Edit details
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={merchantInfo.displayName}
                          onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayName: e.target.value }))}
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                          placeholder="Business Name"
                        />
                        <input
                          type="text"
                          value={merchantInfo.displayAddress}
                          onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayAddress: e.target.value }))}
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                          placeholder="Street Address"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            value={merchantInfo.displayCity}
                            onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayCity: e.target.value }))}
                            className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                            placeholder="City"
                          />
                          <input
                            type="text"
                            value={merchantInfo.displayState}
                            onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayState: e.target.value }))}
                            className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                            placeholder="State"
                          />
                          <input
                            type="text"
                            value={merchantInfo.displayZip}
                            onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayZip: e.target.value }))}
                            className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                            placeholder="ZIP"
                          />
                        </div>
                        <input
                          type="tel"
                          value={merchantInfo.displayPhone}
                          onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayPhone: e.target.value }))}
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                          placeholder="Phone"
                        />
                        <input
                          type="email"
                          value={merchantInfo.displayEmail}
                          onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayEmail: e.target.value }))}
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                          placeholder="Email"
                        />
                        <input
                          type="url"
                          value={merchantInfo.displayWebsite}
                          onChange={(e) => setMerchantInfo(prev => ({ ...prev, displayWebsite: e.target.value }))}
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                          placeholder="Website"
                        />
                        <button
                          type="button"
                          onClick={() => setShowMerchantEdit(false)}
                          className="text-sm font-medium text-brand hover:text-brand-hover transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Dates & Details */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Estimate number</label>
                        <input
                          type="text"
                          value={isEdit ? (estimateData.estimateNumber || estimateNumber) : preGeneratedEstimateNumber}
                          readOnly
                          className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">PO number</label>
                        <input
                          type="text"
                          value={estimateData.poNumber || ''}
                          onChange={(e) => setEstimateData(prev => ({ ...prev, poNumber: e.target.value }))}
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Issue date</label>
                        <DatePicker
                          date={estimateData.issueDate ? parseDateSafely(estimateData.issueDate) : null}
                          onDateChange={(date) => date && handleIssueDateChange(format(date, 'yyyy-MM-dd'))}
                          placeholder="Select date"
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Expiration date</label>
                        <DatePicker
                          date={estimateData.expirationDate ? parseDateSafely(estimateData.expirationDate) : null}
                          onDateChange={(date) => date && setEstimateData(prev => ({ ...prev, expirationDate: format(date, 'yyyy-MM-dd') }))}
                          placeholder="Select date"
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Payment terms (when converted to invoice)</label>
                      <select
                        value={estimateData.paymentTerms}
                        onChange={(e) => handlePaymentTermsChange(e.target.value)}
                        className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow appearance-none cursor-pointer"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                      >
                        <option value="due_on_receipt">Due on Receipt</option>
                        <option value="net_15">Net 15</option>
                        <option value="net_30">Net 30</option>
                        <option value="net_60">Net 60</option>
                        <option value="net_90">Net 90</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Customer Section */}
            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Customer</h2>
                <p className="text-sm text-gray-500 mt-1">Who is this estimate for?</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Customer Selection */}
                  <div className="dropdown-container">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bill to</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        className={cn(
                          "w-full h-11 pl-11 pr-4 bg-white border rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow",
                          formErrors.customer ? "border-red-300" : "border-gray-300"
                        )}
                        value={customerSearch}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCustomerSearch(value);
                          if (selectedCustomer && value !== getCustomerName(selectedCustomer)) {
                            setSelectedCustomer(null);
                            setEstimateData(prev => ({ ...prev, customerId: null }));
                          }
                          setShowCustomerDropdown(true);
                        }}
                        onFocus={() => {
                          setShowCustomerDropdown(true);
                          if (selectedCustomer && !customerSearch) {
                            setCustomerSearch(getCustomerName(selectedCustomer));
                          }
                        }}
                        onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                        placeholder="Search customers..."
                      />

                      {showCustomerDropdown && (
                        <div className="absolute z-30 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                          <div className="max-h-64 overflow-auto">
                            {filteredCustomers.length > 0 ? (
                              <>
                                {filteredCustomers.map(customer => (
                                  <button
                                    key={customer.id}
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); handleCustomerSelect(customer); }}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                                  >
                                    <p className="font-medium text-gray-900">{getCustomerName(customer)}</p>
                                    <p className="text-sm text-gray-500 mt-0.5">{customer.email}</p>
                                  </button>
                                ))}
                                {totalCustomerMatches > DROPDOWN_LIMIT && (
                                  <div className="px-4 py-2 text-center text-xs text-gray-400 border-t border-gray-100">
                                    Showing {DROPDOWN_LIMIT} of {totalCustomerMatches} matches — refine your search
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="px-4 py-6 text-center">
                                <p className="text-sm text-gray-500">No customers found</p>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => { setShowCustomerModal(true); setShowCustomerDropdown(false); }}
                            className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-brand hover:bg-brand-light border-t border-gray-100 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                            Add new customer
                          </button>
                        </div>
                      )}
                    </div>
                    {formErrors.customer && <p className="mt-2 text-sm text-red-600">{formErrors.customer}</p>}

                    {selectedCustomer && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-brand">
                              {getCustomerName(selectedCustomer).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900">{getCustomerName(selectedCustomer)}</p>
                            <p className="text-sm text-gray-500 mt-0.5">{selectedCustomer.email}</p>
                            {selectedCustomer.address && (
                              <p className="text-sm text-gray-500 mt-1">
                                {selectedCustomer.address}
                                {selectedCustomer.city && `, ${selectedCustomer.city}`}
                                {selectedCustomer.state && `, ${selectedCustomer.state}`}
                                {selectedCustomer.zipCode || selectedCustomer.zip
                                  ? ` ${selectedCustomer.zipCode || selectedCustomer.zip}`
                                  : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Shipping */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Shipping address</label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showShipping}
                          onChange={(e) => setShowShipping(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <span className="text-sm text-gray-600">Different address</span>
                      </label>
                    </div>

                    {showShipping ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={estimateData.shipToAddress || ''}
                          onChange={(e) => setEstimateData(prev => ({ ...prev, shipToAddress: e.target.value }))}
                          className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                          placeholder="Street address"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <input type="text" value={estimateData.shipToCity || ''} onChange={(e) => setEstimateData(prev => ({ ...prev, shipToCity: e.target.value }))} className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow" placeholder="City" />
                          <input type="text" value={estimateData.shipToState || ''} onChange={(e) => setEstimateData(prev => ({ ...prev, shipToState: e.target.value }))} className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow" placeholder="State" />
                          <input type="text" value={estimateData.shipToZip || ''} onChange={(e) => setEstimateData(prev => ({ ...prev, shipToZip: e.target.value }))} className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow" placeholder="ZIP" />
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <p className="text-sm text-gray-500">Same as billing address</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Line Items Section */}
            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Line items</h2>
                <p className="text-sm text-gray-500 mt-1">Products and services for this estimate</p>
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Qty</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">Price</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">Amount</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">Tax</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((item, index) => (
                      <tr key={index} className="group">
                        <td className="py-4 px-4 dropdown-container">
                          <div className="relative">
                            <input
                              type="text"
                              value={item.itemName || ''}
                              onChange={(e) => handleItemNameEdit(index, e.target.value, index)}
                              onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                              onBlur={() => setTimeout(() => setShowProductDropdown(prev => ({ ...prev, [index]: false })), 200)}
                              className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                              placeholder="Search or add item..."
                            />
                            {showProductDropdown[index] && (
                              <div className="absolute z-30 w-72 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                {(() => {
                                  const search = (item.itemName || '').toLowerCase().trim();
                                  const genericItems = ['Sales', 'Service', 'Shipping'].filter(t => !search || t.toLowerCase().includes(search));
                                  const hasCatalogRows = buildCatalogPickerRows(products, search, { limit: 8 }).length > 0;

                                  return (
                                    <div className="max-h-64 overflow-auto">
                                      {genericItems.length > 0 && (
                                        <>
                                          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Quick add</span>
                                          </div>
                                          {genericItems.map(type => (
                                            <button
                                              key={type}
                                              type="button"
                                              onClick={() => {
                                                const newItems = [...items];
                                                newItems[index] = {
                                                  ...clearCatalogProductIdentity(newItems[index]),
                                                  itemType: resolveGenericLineItemType(type),
                                                  itemName: type
                                                };
                                                setItems(newItems);
                                                setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                                              }}
                                              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors text-sm text-gray-900"
                                            >
                                              {type}
                                            </button>
                                          ))}
                                        </>
                                      )}
                                      <CatalogProductPickerSection
                                        products={products}
                                        search={search}
                                        limit={8}
                                        onSelectProduct={(product) => {
                                          handleProductSelect(index, product);
                                          setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                                        }}
                                        onSelectVariant={(product, variant) => {
                                          handleVariantSelect(index, product, variant);
                                          setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                                        }}
                                      />
                                      {!genericItems.length && !hasCatalogRows && search && (
                                        <div className="px-4 py-6 text-center text-sm text-gray-500">No items found</div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                            placeholder="Description"
                          />
                        </td>
                        <td className="py-4 px-4">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 text-center focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                            min="0.01"
                            step="any"
                          />
                        </td>
                        <td className="py-4 px-4">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                              className="w-full h-10 pl-7 pr-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-sm font-semibold text-gray-900">{formatCurrency(item.quantity * item.unitPrice)}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={item.isTaxable}
                            onChange={(e) => handleItemChange(index, 'isTaxable', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand"
                          />
                        </td>
                        <td className="py-4 px-4">
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            disabled={items.length === 1}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-100">
                {items.map((item, index) => (
                  <div key={index} className="p-4 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 dropdown-container">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Item</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={item.itemName || ''}
                            onChange={(e) => handleItemNameEdit(index, e.target.value, `mobile_${index}`)}
                            onFocus={() => setShowProductDropdown(prev => ({ ...prev, [`mobile_${index}`]: true }))}
                            onBlur={() => setTimeout(() => setShowProductDropdown(prev => ({ ...prev, [`mobile_${index}`]: false })), 200)}
                            className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                            placeholder="Search or add item..."
                          />
                          {showProductDropdown[`mobile_${index}`] && (
                            <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                              {(() => {
                                const search = (item.itemName || '').toLowerCase().trim();
                                const genericItems = ['Sales', 'Service', 'Shipping'].filter(t => !search || t.toLowerCase().includes(search));
                                const hasCatalogRows = buildCatalogPickerRows(products, search, { limit: 6 }).length > 0;

                                return (
                                  <div className="max-h-64 overflow-auto">
                                    {genericItems.length > 0 && (
                                      <>
                                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                                          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Quick add</span>
                                        </div>
                                        {genericItems.map(type => (
                                          <button
                                            key={type}
                                            type="button"
                                            onClick={() => {
                                              const newItems = [...items];
                                              newItems[index] = {
                                                ...clearCatalogProductIdentity(newItems[index]),
                                                itemType: resolveGenericLineItemType(type),
                                                itemName: type
                                              };
                                              setItems(newItems);
                                              setShowProductDropdown(prev => ({ ...prev, [`mobile_${index}`]: false }));
                                            }}
                                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors text-sm text-gray-900"
                                          >
                                            {type}
                                          </button>
                                        ))}
                                      </>
                                    )}
                                    <CatalogProductPickerSection
                                      products={products}
                                      search={search}
                                      limit={6}
                                      onSelectProduct={(product) => {
                                        handleProductSelect(index, product);
                                        setShowProductDropdown(prev => ({ ...prev, [`mobile_${index}`]: false }));
                                      }}
                                      onSelectVariant={(product, variant) => {
                                        handleVariantSelect(index, product, variant);
                                        setShowProductDropdown(prev => ({ ...prev, [`mobile_${index}`]: false }));
                                      }}
                                    />
                                    {!genericItems.length && !hasCatalogRows && search && (
                                      <div className="px-4 py-6 text-center text-sm text-gray-500">No items found</div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        disabled={items.length === 1}
                        className="mt-6 p-2 text-gray-400 hover:text-red-500 disabled:opacity-30"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Qty</label>
                        <input type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 text-center focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" min="0.01" step="any" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Price</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input type="number" value={item.unitPrice} onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)} className="w-full h-11 pl-7 pr-3 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" min="0" step="0.01" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Total</label>
                        <div className="h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-end">
                          <span className="text-sm font-semibold text-gray-900">{formatCurrency(item.quantity * item.unitPrice)}</span>
                        </div>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={item.isTaxable} onChange={(e) => handleItemChange(index, 'isTaxable', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand" />
                      <span className="text-sm text-gray-600">Taxable</span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="px-6 py-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={addLineItem}
                  className="w-full flex items-center justify-center gap-2 h-11 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-gray-300 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add line item
                </button>
                {formErrors.items && <p className="mt-3 text-sm text-red-600">{formErrors.items}</p>}
              </div>
            </section>

            {/* Notes Section */}
            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Notes</h2>
                <p className="text-sm text-gray-500 mt-1">Additional information for your customer</p>
              </div>
              <div className="p-6">
                <textarea
                  value={estimateData.customerNotes}
                  onChange={(e) => setEstimateData(prev => ({ ...prev, customerNotes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow resize-none"
                  placeholder="Add any notes for your customer..."
                />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden sticky top-[104px]">
              <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Summary</h2>
              </div>
              <div className="p-6 space-y-6">
                {/* Tax & Discount Inputs */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-gray-600">Tax rate</label>
                    <div className="relative w-28">
                      <input
                        type="number"
                        value={estimateData.taxRate}
                        onChange={(e) => setEstimateData(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                        className="w-full h-10 pl-3 pr-8 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="estimate-discount" className="text-sm text-gray-600">Discount</label>
                    <div className="relative w-28">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <input
                        id="estimate-discount"
                        type="number"
                        value={estimateData.discountValue}
                        onChange={(e) => setEstimateData(prev => ({ ...prev, discountType: 'fixed_amount', discountValue: parseFloat(e.target.value) || 0 }))}
                        className="w-full h-10 pl-7 pr-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label htmlFor="estimate-coupon" className="text-sm text-gray-600">Coupon</label>
                      <input
                        id="estimate-coupon"
                        type="text"
                        value={estimateData.discountCode || ''}
                        onChange={(e) => setEstimateData(prev => ({ ...prev, discountCode: e.target.value }))}
                        className="w-28 h-10 px-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        placeholder="Code"
                        autoComplete="off"
                      />
                    </div>
                    <FieldError error={codeFieldError} className="mt-1 justify-end" />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-gray-600">Shipping</label>
                    <div className="relative w-28">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <input
                        type="number"
                        value={estimateData.shippingCost}
                        onChange={(e) => setEstimateData(prev => ({ ...prev, shippingCost: parseFloat(e.target.value) || 0 }))}
                        className="w-full h-10 pl-7 pr-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div className="h-px bg-gray-100" />

                {/* Totals */}
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-medium text-gray-900">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {estimateData.taxRate > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Tax ({estimateData.taxRate}%)</span>
                      <span className="font-medium text-gray-900">{formatCurrency(totals.taxAmount)}</span>
                    </div>
                  )}
                  {previewNamedDiscounts.map((row, index) => (
                    <div key={`named-discount-${index}`} className="flex justify-between text-sm">
                      <span className="text-gray-500">{row.title}</span>
                      <span className="font-medium text-red-600">-{formatCurrency(row.amount)}</span>
                    </div>
                  ))}
                  {estimateData.shippingCost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Shipping</span>
                      <span className="font-medium text-gray-900">{formatCurrency(estimateData.shippingCost)}</span>
                    </div>
                  )}
                </div>

                <div className="h-px bg-gray-200" />

                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900">Estimated Total</span>
                  <span className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Footer Actions */}
        <div className="xl:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3">
          <button
            onClick={() => handleSave('save')}
            disabled={saving}
            className="flex-1 h-12 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Save draft
          </button>
          <button
            onClick={() => handleSave('send')}
            disabled={saving}
            className="flex-1 h-12 px-4 text-sm font-semibold text-gray-900 bg-brand rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-50"
          >
            Save & Send
          </button>
        </div>
      </main>

      <CustomerCreateModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerCreated={(customer) => {
          setCustomers(prev => [...prev, customer]);
          handleCustomerSelect(customer);
          setShowCustomerModal(false);
        }}
      />
    </div>
  );
}

export default EstimateForm;
